import { useState, type ReactNode } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Coins } from 'lucide-react'
import { Button } from '@/shared/components/ui/button'
import { Card } from '@/shared/components/ui/card'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/components/ui/tabs'
import { QueryErrorScreen } from '@/shared/components/ui/query-error-screen'
import { Pagination } from '@/shared/components/ui/pagination'
import { RefreshButton } from '@/shared/components/ui/refresh-button'
import { routes, DEFAULT_PAGE_SIZE } from '@/shared/constants'
import { useMyBusinesses } from '@/features/businesses'
import { useBusinessBalance } from './hooks/useBusinessBalance'
import { useCreditMovements } from './hooks/useCreditMovements'
import { useDeviceRecharges } from './hooks/useDeviceRecharges'
import { useDeliveryChannels } from './hooks/useDeliveryChannels'
import { useCurrencies } from './hooks/useCurrencies'
import { CreditsBusinessPicker } from './components/CreditsBusinessPicker'
import { BalanceCard } from './components/BalanceCard'
import { PurchaseRechargeDialog } from './components/PurchaseRechargeDialog'
import { CreditMovementsTable } from './components/CreditMovementsTable'
import { DeviceRechargesTable } from './components/DeviceRechargesTable'
import { BalanceCardSkeleton, MovementsTableSkeleton } from './components/CreditsSkeletons'

const PAGE_SIZE = DEFAULT_PAGE_SIZE

function EmptyRow({ text }: { text: string }) {
  return <p className="py-10 text-center text-sm text-ink-muted">{text}</p>
}

function CreditFilters({ children }: { children: ReactNode }) {
  return <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end">{children}</div>
}

function FilterSelect({
  label,
  value,
  onChange,
  options,
}: {
  label: string
  value: string
  onChange: (v: string) => void
  options: { value: string; label: string }[]
}) {
  return (
    <div className="w-full sm:max-w-[200px]">
      <label className="mb-1.5 block text-xs font-medium text-ink-muted">{label}</label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-10 w-full rounded-lg border border-line bg-white px-3 text-sm text-ink outline-none focus:border-brand"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </div>
  )
}

export function CreditsPage() {
  const navigate = useNavigate()
  const [params, setParams] = useSearchParams()
  const [dialogOpen, setDialogOpen] = useState(false)
  const [movPage, setMovPage] = useState(1)
  const [rchPage, setRchPage] = useState(1)

  // Filtro de movimientos por tipo (canal). El gateway no soporta filtro por
  // rango de fecha ni por estado de recarga, así que esos filtros no se ofrecen.
  const [movChannel, setMovChannel] = useState('')

  const { channels } = useDeliveryChannels()

  const {
    businesses,
    loading: bizLoading,
    error: bizError,
    refetch: refetchBiz,
  } = useMyBusinesses({ take: 50, skip: 0 })

  // El negocio elegido vive en `?businessId=` (compartible); por defecto el primero.
  const selectedBusinessId = params.get('businessId') ?? businesses[0]?.id

  const { currencyNameById } = useCurrencies()
  const { balance, loading: balanceLoading, refetch: refetchBalance } =
    useBusinessBalance(selectedBusinessId)
  const {
    movements,
    totalItems: movTotal,
    loading: movLoading,
    refetch: refetchMovements,
  } = useCreditMovements({
    businessId: selectedBusinessId,
    take: PAGE_SIZE,
    skip: (movPage - 1) * PAGE_SIZE,
    channel: movChannel || undefined,
  })
  const {
    recharges,
    totalItems: rchTotal,
    loading: rchLoading,
    refetch: refetchRecharges,
  } = useDeviceRecharges({
    businessId: selectedBusinessId,
    take: PAGE_SIZE,
    skip: (rchPage - 1) * PAGE_SIZE,
  })

  const handleSelectBusiness = (id: string) => {
    const next = new URLSearchParams(params)
    next.set('businessId', id)
    setParams(next, { replace: true })
    setMovPage(1)
    setRchPage(1)
  }

  const handlePurchaseSuccess = () => {
    void refetchBalance()
    void refetchMovements()
    void refetchRecharges()
  }

  // Sin negocios cargables → pantalla de error (no hay nada que mostrar).
  if (bizError && businesses.length === 0) {
    return <QueryErrorScreen error={bizError} onRetry={() => refetchBiz()} />
  }

  const noBusinesses = !bizLoading && businesses.length === 0

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-serif text-[36px] font-normal leading-[1.1] tracking-tight text-ink">
            Créditos y recargas
          </h1>
          <p className="mt-1 text-sm text-ink-muted">
            Consulta el balance de cada negocio, su historial de movimientos y compra créditos.
          </p>
        </div>
        <RefreshButton
          onClick={async () => {
            await Promise.all([refetchBalance(), refetchMovements(), refetchRecharges()])
          }}
        />
      </header>

      {noBusinesses ? (
        <Card className="flex flex-col items-center gap-3 py-12 text-center">
          <div className="grid h-12 w-12 place-items-center rounded-full bg-brand-soft text-brand">
            <Coins className="h-6 w-6" />
          </div>
          <div>
            <p className="font-semibold text-ink">Aún no tienes negocios</p>
            <p className="mt-1 text-sm text-ink-muted">
              Crea tu primer negocio para gestionar créditos y recargas.
            </p>
          </div>
          <Button variant="primary" onClick={() => navigate(routes.app.businesses.new)}>
            Crear negocio
          </Button>
        </Card>
      ) : (
        <>
          <CreditsBusinessPicker
            businesses={businesses}
            value={selectedBusinessId}
            onChange={handleSelectBusiness}
            disabled={bizLoading && businesses.length === 0}
          />

          {balanceLoading ? (
            <BalanceCardSkeleton />
          ) : (
            <BalanceCard
              balance={balance}
              onPurchase={() => setDialogOpen(true)}
              disabled={!selectedBusinessId}
            />
          )}

          <Card>
            <Tabs defaultValue="movements">
              <TabsList>
                <TabsTrigger value="movements">Movimientos</TabsTrigger>
                <TabsTrigger value="recharges">Recargas a dispositivos</TabsTrigger>
              </TabsList>

              <TabsContent value="movements">
                <CreditFilters>
                  <FilterSelect
                    label="Tipo"
                    value={movChannel}
                    onChange={(v) => { setMovChannel(v); setMovPage(1) }}
                    options={[{ value: '', label: 'Todos' }, ...channels.map((c) => ({ value: c.channel, label: c.channel }))]}
                  />
                </CreditFilters>
                {movLoading ? (
                  <MovementsTableSkeleton />
                ) : movements.length === 0 ? (
                  <EmptyRow text="Sin movimientos con estos filtros." />
                ) : (
                  <>
                    <CreditMovementsTable
                      movements={movements}
                      currencyNameById={currencyNameById}
                    />
                    <Pagination
                      page={movPage}
                      pageSize={PAGE_SIZE}
                      totalItems={movTotal}
                      onPageChange={setMovPage}
                    />
                  </>
                )}
              </TabsContent>

              <TabsContent value="recharges">
                {rchLoading ? (
                  <MovementsTableSkeleton />
                ) : recharges.length === 0 ? (
                  <EmptyRow text="Sin recargas todavía." />
                ) : (
                  <>
                    <DeviceRechargesTable
                      recharges={recharges}
                      currencyNameById={currencyNameById}
                    />
                    <Pagination
                      page={rchPage}
                      pageSize={PAGE_SIZE}
                      totalItems={rchTotal}
                      onPageChange={setRchPage}
                    />
                  </>
                )}
              </TabsContent>
            </Tabs>
          </Card>
        </>
      )}

      {selectedBusinessId && (
        <PurchaseRechargeDialog
          businessId={selectedBusinessId}
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          onSuccess={handlePurchaseSuccess}
        />
      )}
    </div>
  )
}
